import React from 'react';

import { ScrollView } from 'react-native';

import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';
import FullWidthAutoHeightImage from 'components/picture/full-width-auto-height-image';
import { ProductUpsell } from 'components/product-detail-v2/product-upsell';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { getFirstOption, isCombo, isItem } from 'state/unified-menu/utils';
import { primitive } from 'styles/constants/primitives';

import { useProductWizardContext } from '../../product-wizard';
import { ProductWizardStep } from '../../product-wizard/types';
import { useProductWizardFlow } from '../../product-wizard/use-product-wizard-flow';
import { PickerAspectSelector } from '../picker-aspect-selector';
import { ProductCombo } from '../product-combo';
import { ProductCta } from '../product-cta';
import { ProductDetailHeader } from '../product-detail-flow/product-detail-header';
import { ProductItem } from '../product-detail-flow/product-item';
import { ProductInformation } from '../product-information';
import { ProductNutritionInformation } from '../product-nutrition-information/product-nutrition-information';
import { ProductOverview } from '../product-overview';

import {
  ProductHomeInnerLayout,
  ProductHomeLayoutContainer,
  ProductInformationSection,
  ProductSelectionsContainer,
} from './styled';

export const ProductHome = () => {
  const isMobile = useIsMobileBreakpoint();

  const { currentFlowStep, cartIdEditing, id } = useProductWizardFlow();
  const { backgroundColor: takeoverBg, backgroundImage: takeoverBgImage } = useAppTakeoverPdp(id);

  const { menuObject, selectedProduct } = useProductWizardContext();

  const selectedProductName = selectedProduct.name?.locale ?? '';

  const getHeaderTitle = () => {
    if (currentFlowStep.step !== ProductWizardStep.PickerReview) {
      return selectedProductName;
    }

    if (isCombo(selectedProduct)) {
      return t('reviewYourMeal');
    }

    return t('reviewYourSelection');
  };

  const productOverviewTitle =
    currentFlowStep.step === ProductWizardStep.PickerReview ? selectedProductName : undefined;

  const showInfo =
    currentFlowStep.step === ProductWizardStep.ProductHome ||
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    cartIdEditing;

  const showProductUpsell =
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    isCombo(menuObject) ||
    isItem(menuObject);

  const showProductInformation = currentFlowStep.step === ProductWizardStep.ProductHome;

  return (
    <>
      <ProductDetailHeader title={getHeaderTitle()} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ backgroundColor: takeoverBg ?? primitive.$white }}
      >
        {takeoverBgImage && (
          <FullWidthAutoHeightImage source={takeoverBgImage} style={{ position: 'absolute' }} />
        )}
        {showInfo && <ProductOverview productTitle={productOverviewTitle} />}
        <ProductSelectionsContainer testID="product-home-customization-panel">
          <ProductHomeLayoutContainer isFullContainer={isMobile}>
            <ProductHomeInnerLayout $isFullContainer={isMobile}>
              <ProductHomeContent />
            </ProductHomeInnerLayout>
          </ProductHomeLayoutContainer>
        </ProductSelectionsContainer>
        {showInfo && (
          <ProductHomeLayoutContainer
            isFullContainer={isMobile}
            backgroundColor={takeoverBg ?? primitive.bk.$mayoEggWhite}
          >
            <ProductHomeInnerLayout>
              <ProductInformationSection>
                {showProductUpsell && <ProductUpsell />}
                {showProductInformation && <ProductInformation selectedProduct={selectedProduct} />}
                <ProductNutritionInformation selectedProduct={selectedProduct} />
              </ProductInformationSection>
            </ProductHomeInnerLayout>
          </ProductHomeLayoutContainer>
        )}
      </ScrollView>
      <ProductCta />
    </>
  );
};

export const ProductHomeContent = () => {
  const { currentFlowStep, cartIdEditing } = useProductWizardFlow();
  const { menuObject } = useProductWizardContext();

  if (isItem(menuObject)) {
    return <ProductItem />;
  }

  if (
    isCombo(menuObject) ||
    currentFlowStep.step === ProductWizardStep.PickerReview ||
    !!cartIdEditing
  ) {
    return <ProductCombo />;
  }

  const isFirstAspect = currentFlowStep.step === ProductWizardStep.ProductHome;
  const pickerAspectId = currentFlowStep.pickerAspectKey || getFirstOption(menuObject).entryId;
  return <PickerAspectSelector pickerAspectId={pickerAspectId} isFirstAspect={isFirstAspect} />;
};
